import {
  MISSING_SHOPPING_BASKET_ID_ERROR,
  isAShoppingBasketId,
} from '#src/utils';
import React, { createContext, useContext } from 'react';

import {
  useGetShoppingBasketByIdParam,
  useGetShoppingBasketByTokenParam,
} from './ShoppingBasketInfoProvider.hook';
import { ShoppingBasketHook } from './types';

const ShoppingBasketContext = createContext<ShoppingBasketHook | null>(null);

export const useShoppingBasketInfo = () => useContext(ShoppingBasketContext);
export const useShoppingBasketInfoWithDefaultData = () => {
  const shoppingBasketInfo = useShoppingBasketInfo();
  return getShoppingBasketDefaultData(shoppingBasketInfo);
};

export const getShoppingBasketDefaultData = (
  shoppingBasketInfo
): ShoppingBasketHook => {
  const {
    products = [],
    bundles = [],
    loading = true,
    ...rest
  } = shoppingBasketInfo ?? {
    products: [],
    bundles: [],
    loading: true,
  };
  return { bundles, products, loading, ...rest };
};

type ShoppingBasketInfoProviderProps = {
  children: React.ReactNode;
  shoppingBasketId: string;
};

const ShoppingBasketInfoProviderWithId = ({
  children,
  shoppingBasketId,
}: ShoppingBasketInfoProviderProps) => {
  const data = useGetShoppingBasketByIdParam({
    shoppingBasketId,
  });

  return (
    <ShoppingBasketContext.Provider value={data}>
      {children}
    </ShoppingBasketContext.Provider>
  );
};

const ShoppingBasketInfoProviderWithToken = ({
  children,
  shoppingBasketId,
}: ShoppingBasketInfoProviderProps) => {
  const data = useGetShoppingBasketByTokenParam({
    shoppingBasketId,
  });

  return (
    <ShoppingBasketContext.Provider value={data}>
      {children}
    </ShoppingBasketContext.Provider>
  );
};

export const ShoppingBasketInfoProvider = ({
  children,
  shoppingBasketId,
}: ShoppingBasketInfoProviderProps) => {
  if (!shoppingBasketId) {
    throw new Error(MISSING_SHOPPING_BASKET_ID_ERROR);
  }

  const Provider = isAShoppingBasketId(shoppingBasketId)
    ? ShoppingBasketInfoProviderWithId
    : ShoppingBasketInfoProviderWithToken;

  return <Provider shoppingBasketId={shoppingBasketId}>{children}</Provider>;
};
